/* eslint-disable react/jsx-no-duplicate-props */
import { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import CircleIcon from "@mui/icons-material/Circle";
import OpenIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowRightSharpIcon from "@mui/icons-material/KeyboardArrowRightSharp";
import ICompAnalysis from "../../../types/ICompAnalysis";
import FreightDetails from "./FreightDetails";
import NumberTextfield from "../../../components/NumberTextfield";
import "./KochOrigin.css";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import convertRailFreightDetailData from "../../../utils/convertRailFreightDetailData";
import IRailFreightDetails from "../../../types/IRailFreightDetails";
import { useIsMobile } from "../../../hooks/useIsMobile";

const green = "#C0FFBD";
const yellow = "#FFFF83";
const red = "#FE8686A3";

const KochOrigin = ({
  record,
  hidden,
  activeFreightButton,
  setActiveFreightButton,
}: {
  record: ICompAnalysis;
  hidden?: boolean;
  activeFreightButton: any;
  setActiveFreightButton: (value: any) => void;
}) => {
  const [
    setSelectedRailDetailRecord,
    setDisplayFreightDetailsPanel,
    displayFreightDetailsPanel,
    selectYourOfferFreight,
    setSelectYourOfferFreight,
    setCloseFreightDetailsPanel,
    kochOffer,
  ] = useNitroCAStore((state) => [
    state.setSelectedRailDetailRecord,
    state.setDisplayRailFreightDetailsPanel,
    state.displayRailFreightDetailsPanel,
    state.selectYourOfferFreight,
    state.setSelectYourOfferFreight,
    state.setCloseRailFreightDetailsPanel,
    state.kochOffer,
  ]);

  const gray = "#ECEDEF";
  const isMobile = useIsMobile(768);
  const mobileStyle = isMobile
    ? {
        "& .MuiInputBase-input": {
          minWidth: isMobile ? "20px" : "auto",
        },
      }
    : {};

  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState(
    Math.round(kochOffer ?? record.initialOffer),
  );
  const [netback, setNetback] = useState(record.price);
  // when favorite origins are updated and comp analysis is reloaded then reset offer and netback
  useEffect(() => {
    setOffer(record.initialOffer);
    setNetback(record.price);
  }, [record]);
  useEffect(() => {
    if (kochOffer) {
      setOffer(kochOffer);
      setNetback(Math.round(kochOffer - record.freight));
    }
  }, [kochOffer, record.freight]);
  const isButtonActive =
    activeFreightButton &&
    activeFreightButton.id === record.id &&
    displayFreightDetailsPanel &&
    !selectYourOfferFreight &&
    activeFreightButton.type === "koch";

  const iconButtonStyle = {
    padding: "4px 2px",
    borderRadius: 6,
    backgroundColor: isButtonActive ? "#09B0F2" : "transparent",
  };
  const textStyle = {
    fontSize: 14,
    color: isButtonActive ? "white" : "#005CA6",
    display: "flex",
    alignItems: "center",
    paddingLeft: "10px",
  };

  const [color, setColor] = useState(gray);
  useEffect(() => {
    if (!record) {
      setNetback(0);
      return;
    }
    const minPrice = record.minPrice || record.maxPrice;
    if (record.maxPrice) {
      if (netback > record.maxPrice) {
        setColor(green);
      } else if (netback === record.maxPrice) {
        setColor(gray);
      } else if (netback >= minPrice) {
        setColor(yellow);
      } else {
        setColor(red);
      }
    } else {
      setColor(gray);
    }
  }, [offer, netback, record, gray]);

  if (hidden) return null;
  let railFreightDetailData: IRailFreightDetails;

  return (
    <Paper
      style={{
        boxShadow: "none",
        backgroundColor: "#F7F7F7",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        borderRadius: 10,
        border: "1px solid #d9d9d9",
        padding: "10px 4px",
      }}
    >
      {/* HEADING */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
          position: "relative",
          padding: "0px 5px",
        }}
      >
        <div
          style={{
            fontWeight: 500,
            fontSize: 16,
            overflow: "hidden",
            textOverflow: "ellipsis",
            textWrap: "nowrap",
            maxWidth: 260,
          }}
        >
          {record?.origin?.name}
        </div>
        <div style={{ height: 14, width: 1, backgroundColor: "black" }} />
        <div style={{ fontWeight: 400, fontSize: 14 }}>{record.mode}</div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <NumberTextfield
            size="small"
            value={Math.round(offer)}
            setValue={(value) => {
              if (value > 9999 || value < -9999) {
                return;
              }
              setOffer(value);

              if (Number.isNaN(value)) {
                setNetback(record.freight * -1);
              } else {
                setNetback(Math.round(Number(value) - record.freight));
              }
            }}
            decimalPlaces={0}
            InputProps={{
              style: {
                padding: "0px 3px",
                fontSize: 15,
              },
              startAdornment: (
                <InputAdornment className="DollarAdorment" position="start">
                  <div>$</div>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment className="PriceAdornment" position="end">
                  <div>DLVD</div>
                </InputAdornment>
              ),
            }}
            style={{ width: 95 }}
          />
        </div>
        <CircleIcon
          style={{ fontSize: 8, color: "#D9D9D9", padding: "0px 6px" }}
        />

        {record.freight === 0 && (
          <div style={{ fontSize: 14 }}>Ask Logistics</div>
        )}

        {record.freight > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              className="FreightContainer"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                columnGap: 2,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: 60,
                  height: 60,
                }}
              >
                {record.mode === "RAIL" && !record.isValid && (
                  <div
                    style={{
                      zIndex: 1,
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <svg
                      width="60"
                      height="50"
                      viewBox="0 0 50 42"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M48.2132 19.9692C48.5946 20.6035 48.5946 21.3965 48.2132 22.0308L37.5174 39.8154C37.1557 40.4168 36.5053 40.7846 35.8035 40.7846H14.1965C13.4947 40.7846 12.8443 40.4168 12.4826 39.8154L1.78682 22.0308C1.40538 21.3965 1.40538 20.6035 1.78682 19.9692L12.4826 2.18463C12.8443 1.58322 13.4947 1.21539 14.1965 1.21539H35.8035C36.5053 1.21539 37.1557 1.58322 37.5174 2.18463L48.2132 19.9692Z"
                        fill="#FFE7E7"
                        stroke="#FF4646"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                )}

                <div
                  style={{
                    zIndex: 2,
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: 12,
                  }}
                >{`$${record.freight}`}</div>
              </div>
              <Tooltip title="Click to view Freight Details" placement="bottom">
                <IconButton
                  onClick={() => {
                    setActiveFreightButton({ id: record.id, type: "koch" });
                    if (record.mode === "RAIL") {
                      railFreightDetailData = convertRailFreightDetailData({
                        ...record,
                      });
                      setSelectedRailDetailRecord(railFreightDetailData);
                      setDisplayFreightDetailsPanel(true);
                      setCloseFreightDetailsPanel(false);
                      setOpen(false);
                      setSelectYourOfferFreight(false);
                    } else if (record.mode === "TRUCK") {
                      setOpen(!open);
                    }
                  }}
                  style={iconButtonStyle}
                >
                  <div style={textStyle}>
                    Freight
                    {(() => {
                      if (open) {
                        return <CloseIcon />;
                      }
                      if (record.mode === "TRUCK") {
                        return <OpenIcon />;
                      }
                      if (record.mode === "RAIL") {
                        return <KeyboardArrowRightSharpIcon />;
                      }
                      return null;
                    })()}
                  </div>
                </IconButton>
              </Tooltip>
            </div>
          </div>
        )}

        <CircleIcon
          style={{ fontSize: 8, color: "#D9D9D9", padding: "0px 6px" }}
        />

        <NumberTextfield
          size="small"
          value={netback}
          setValue={(value) => {
            if (value > 9999 || value < -9999) {
              return;
            }
            setNetback(value);
            setOffer(Math.round(Number(value) + record.freight));
          }}
          decimalPlaces={0}
          sx={{
            padding: 0,
            minWidth: isMobile ? "80px" : "auto",
            ...mobileStyle,
          }}
          InputProps={{
            style: {
              padding: "0px 3px",
              fontSize: 15,
              backgroundColor: color,
            },

            startAdornment: (
              <InputAdornment className="DollarAdorment" position="start">
                $
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment className="PriceAdornment" position="end">
                <div>FOB</div>
              </InputAdornment>
            ),
          }}
          style={{ width: 90 }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
        }}
      >
        {record.maxPrice && (
          <>
            <div style={{ fontWeight: 500, fontSize: 16, color: "#4b4b4b" }}>
              {`$${record.maxPrice || 0} - $${
                record.minPrice || record.maxPrice
              }`}
            </div>
            <div
              style={{
                fontWeight: 400,
                fontSize: 14,
                color: "#787878",
              }}
            >
              Guidance
            </div>
          </>
        )}
        {!record.maxPrice && (
          <div
            style={{
              fontWeight: 500,
              fontSize: 16,
              color: "#4b4b4b",
              fontStyle: "italic",
            }}
          >
            No Product Guidance
          </div>
        )}
      </div>

      {open && <FreightDetails record={record} />}
    </Paper>
  );
};

export default KochOrigin;
